import { useCallback } from "react";
import {
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";

import { I18nKeyType } from "@libs/i18n";
import {
  RmFormControl,
  RmFormDescription,
  RmFormErrorMessage,
  RmFormField,
  RmFormItem,
  RmFormLabel,
} from "@/components/RmForm";
import { cn } from "@/utils/tailwind";

import { BaseMultiTextSelect } from "./BaseMultiTextSelect";
import { BaseTextSelect, BaseTextSelectPropsType } from "./BaseTextSelect";

type ApSelectInputPropsType<T extends FieldValues> = BaseTextSelectPropsType & {
  name: Path<T>;
  control: Control<T>;
  labelI18nKey?: I18nKeyType;
  labelI18nOptions?: object;
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
  errorI18nKey?: I18nKeyType;
  errorI18nOptions?: object;
  className?: string;
  showError?: boolean;
  isMultiSelect?: boolean;
  isRequired?: boolean;
  onSelectionChanged?: (value: string | number | (string | number)[]) => void;
};

export const RmSelectInput = <T extends FieldValues>({
  name,
  control,
  labelI18nKey,
  labelI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  className,
  showError = true,
  errorI18nKey,
  errorI18nOptions,
  isRequired = true,
  onSelectionChanged,
  ...props
}: ApSelectInputPropsType<T>) => {
  const { isMultiSelect } = props;

  const renderSelectComponent = useCallback(
    (field: ControllerRenderProps<T, Path<T>>) => {
      const { ...fieldProps } = field;
      switch (true) {
        case isMultiSelect:
          return (
            <BaseMultiTextSelect
              values={field.value ?? []}
              onSelectionChange={values => {
                field.onChange(values);
                onSelectionChanged?.(values);
              }}
              {...props}
              {...fieldProps}
            />
          );

        default:
          return (
            <BaseTextSelect
              onValueChange={selection => {
                field.onChange(selection?.value);
                if (selection) {
                  onSelectionChanged?.(selection?.value);
                }
              }}
              {...props}
              {...fieldProps}
            />
          );
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMultiSelect, props.options, props.isDisabled],
  );

  return (
    <RmFormField
      control={control}
      name={name}
      render={({ field }) => (
        <RmFormItem>
          <div className={cn("flex flex-col gap-2", className)}>
            {labelI18nKey && (
              <RmFormLabel
                i18nKey={labelI18nKey}
                i18nOptions={labelI18nOptions}
                isDisabled={props.isDisabled}
                isRequired={isRequired}
              />
            )}
            <RmFormControl>{renderSelectComponent(field)}</RmFormControl>
            {descriptionI18nKey && (
              <RmFormDescription
                i18nKey={descriptionI18nKey}
                i18nOptions={descriptionI18nOptions}
              />
            )}
          </div>
          {showError && (
            <RmFormErrorMessage
              i18nKey={errorI18nKey}
              i18nOptions={errorI18nOptions}
            />
          )}
        </RmFormItem>
      )}
    />
  );
};
